import { baseApi } from 'api/baseApi';
import type { TFindObjectsPayload } from 'slices/object.slice';
import { IAPIResult } from 'types/state.interface';
import { TTEObject } from '@timeedit/types';

const RELATION_PAGE_LIMIT = 200;

/** Pass-through filter coming from the ObjectTable filter row. */
export type RelationFilter = Pick<
  TFindObjectsPayload,
  | 'typeId'
  | 'fields'
  | 'organizations'
  | 'active'
  | 'extIds'
  | 'modified'
  | 'objectStatus'
  | 'abstract'
  | 'virtual'
  | 'sort'
  | 'sortFieldId'
  | 'includeTemplateObjects'
>;

export type FindRelationsArgs = {
  /** Types of the parent (currently-edited) object. Only the first is sent — backend ignores the rest. */
  parentTypeIds: number[];
  /** ID of the currently-edited parent object. */
  parentObjectId: number;
  /** 1-based page. Becomes an opaque cursor when the backend ships one. */
  page: number;
  /** IDs to omit from the response (current pending-removals). NOT part of the cache key. */
  excludeIds: number[];
  /** Filter from the ObjectTable filter row (typeId, search, etc.). Part of the cache key. */
  filter?: RelationFilter;
};

export type FindRelationsResponse = {
  data: TTEObject[];
  /** `null` when no further pages exist. */
  nextCursor: number | null;
  /**
   * Server-reported count for the request. NOTE: this is post-`excludeIds` (the server has
   * already filtered them out), so callers that want the stable "all-relations" count should
   * add `excludeIds.length` back in.
   */
  totalResults: number;
};

const buildRequestBody = ({
  parentTypeIds,
  parentObjectId,
  page,
  excludeIds,
  filter,
}: FindRelationsArgs) => ({
  // New payload shape — backend confirmed support.
  relations: {
    typeId: parentTypeIds[0], // Only the first type is relevant for relation listing, even if the object has multiple types. The backend will ignore the rest.
    objectIds: [parentObjectId],
  },
  excludeIds: [parentObjectId, ...excludeIds],
  page,
  limit: RELATION_PAGE_LIMIT,
  ...(filter ?? {}),
});

export const relationsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    findRelations: build.query<FindRelationsResponse, FindRelationsArgs>({
      query: (args) => ({
        method: 'POST',
        url: '/objects/find',
        body: buildRequestBody(args),
      }),
      transformResponse: (
        response: IAPIResult<TTEObject>
      ): FindRelationsResponse => {
        const { results, page, totalPages, totalResults } = response;
        return {
          data: results,
          nextCursor: page < totalPages ? page + 1 : null,
          totalResults,
        };
      },
      // Drop `excludeIds` from the cache key so changes to pending-removals don't refetch
      // existing pages — they only affect *future* page fetches via the request body.
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const { excludeIds: _ignored, ...rest } = queryArgs;
        return `${endpointName}:${JSON.stringify(rest)}`;
      },
    }),
  }),
  overrideExisting: false,
});

export const { useLazyFindRelationsQuery } = relationsApi;
export { RELATION_PAGE_LIMIT };
